/**
 * Resolving report parameter defaults that name a stored procedure.
 *
 * A `DefaultValue` matching `^[a-z]stp_` names a procedure that produces the
 * default rather than being the default itself. Executing it is a server
 * concern, and the framework already exposes it through the `WebsiteCMS`
 * `api/reporting/filters` route.
 */

import { isProcedureDefault } from "./parameters.js";
import type { ReportParameter, ReportParameterValue, ReportParameterValues } from "./types.js";

/** A result set as the reporting route serializes it. */
type ProcedureResult = Record<string, unknown[]> | unknown[];

/**
 * Runs the stored procedure a parameter's default names and returns the value
 * it produces — the first column of the first row, the way the Windows launcher
 * reads it.
 *
 * Returns `null` when the parameter has no procedure default or the procedure
 * produced no rows.
 */
export async function fetchParameterDefault(
	reportId: string,
	parameter: ReportParameter,
	signal?: AbortSignal,
): Promise<ReportParameterValue> {
	if (!parameter.defaultValue || !isProcedureDefault(parameter.defaultValue)) return null;

	let response = await fetch(`/api/reporting/filters/${encodeURIComponent(reportId)}/GetParameterDefaultValue`, {
		body: JSON.stringify({ parameterName: parameter.name }),
		credentials: "same-origin",
		headers: {
			"Content-Type": "application/json",
			// The route rejects anything that is not an ajax request, as CSRF
			// protection.
			"X-Requested-With": "XMLHttpRequest",
		},
		method: "POST",
		signal,
	});

	if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

	let result = (await response.json()) as ProcedureResult;

	return firstValue(result);
}

/**
 * Resolves every procedure default of a report in parallel and merges the
 * results over the literal defaults.
 *
 * A parameter whose procedure fails is left at its literal default rather than
 * failing the whole form — the user can still type a value.
 */
export async function resolveParameterDefaults(
	reportId: string,
	parameters: ReportParameter[],
	literalDefaults: ReportParameterValues,
	signal?: AbortSignal,
): Promise<ReportParameterValues> {
	let dynamic = parameters.filter((parameter) => isProcedureDefault(parameter.defaultValue ?? ""));

	if (dynamic.length === 0) return literalDefaults;

	let resolved = await Promise.all(
		dynamic.map(async (parameter) => {
			try {
				return [parameter.name, await fetchParameterDefault(reportId, parameter, signal)] as const;
			} catch {
				return [parameter.name, null] as const;
			}
		}),
	);

	let values: ReportParameterValues = { ...literalDefaults };

	for (let [name, value] of resolved) {
		if (value !== null) values[name] = value;
	}

	return values;
}

/** The first column of the first row of whichever result set came back. */
function firstValue(result: ProcedureResult): ReportParameterValue {
	let rows = Array.isArray(result) ? result : Object.values(result).find((value) => Array.isArray(value));
	let row = rows?.[0];

	if (row === undefined || row === null) return null;
	if (Array.isArray(row)) return (row[0] as ReportParameterValue) ?? null;
	if (typeof row === "object") {
		let values = Object.values(row as Record<string, unknown>);

		return (values[0] as ReportParameterValue) ?? null;
	}

	return row as ReportParameterValue;
}
